// The "Financials" tab of a shipment, a customer, an agent and a carrier. Each shows only the money that belongs to it.
import { useState } from 'react';
import { Box, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import AccountBalanceWalletOutlined from '@mui/icons-material/AccountBalanceWalletOutlined';
import LocalShippingOutlined from '@mui/icons-material/LocalShippingOutlined';
import PaymentsOutlined from '@mui/icons-material/PaymentsOutlined';
import PercentOutlined from '@mui/icons-material/PercentOutlined';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import TrendingUpOutlined from '@mui/icons-material/TrendingUpOutlined';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import { BarChart } from '../../components/BarChart';
import { EmptyState } from '../../components/EmptyState';
import { KeyValueList } from '../../components/KeyValueList';
import { Panel } from '../../components/Panel';
import { StatusChip } from '../../components/StatusChip';
import { jod, percent, priced } from '../../lib/currency';
import { shortDate } from '../../lib/dates';
import { StageStepper } from '../shipments/StageStepper';
import { monthName, STATUS_TONE, summarize } from './calc';
import { FinanceTiles, ProfitTiles } from './FinanceTiles';
import { ProfitChartPanel, ShipmentSummaryPanel } from './FinancePanels';
import { useFinance } from './useFinance';

const FIT = {
  display: 'grid',
  gap: 2,
  alignItems: 'start',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))',
};

/** One shipment's price, costs, estimated profit, status and invoices. USD (the quote) or JOD (the books). */
export function ShipmentFinancials({ srb }: { srb: string }) {
  const [shown, setShown] = useState<'USD' | 'JOD'>('USD');
  const f = useFinance().find((x) => x.r.srb === srb);
  if (!f)
    return (
      <Panel title="Financials">
        <EmptyState>No financial record for this shipment yet.</EmptyState>
      </Panel>
    );
  const { r } = f;
  const money = (jodValue: number | null, own: number | null) =>
    jodValue === null || own === null ? 'Missing' : shown === 'JOD' ? jod(jodValue) : priced(own, r.currency);
  const costOwn = r.agent === null || r.carrier === null || r.local === null ? null : r.agent + r.carrier + r.local;
  const done = [true, r.jcs === 'Approved', f.invoiced, r.status === 'Paid'];
  const current = done.findIndex((d) => !d);

  return (
    <Stack spacing={2}>
      <Box sx={FIT}>
        <Panel title="Financial Summary">
          <ToggleButtonGroup
            exclusive
            size="small"
            color="primary"
            value={shown}
            onChange={(_e, v: 'USD' | 'JOD' | null) => v && setShown(v)}
            sx={{ alignSelf: 'flex-end' }}
          >
            <ToggleButton value="USD" sx={{ px: 1.5, py: 0.25 }}>
              {r.currency}
            </ToggleButton>
            <ToggleButton value="JOD" sx={{ px: 1.5, py: 0.25 }}>
              JOD
            </ToggleButton>
          </ToggleButtonGroup>
          <KeyValueList
            rows={[
              { label: 'Customer Selling Price', value: money(f.revenue, r.price), bold: true },
              { label: 'Agent Charges', value: money(r.agent === null ? null : f.agentJod, r.agent) },
              { label: 'Carrier Charges', value: money(r.carrier === null ? null : f.carrierJod, r.carrier) },
              { label: 'Local Costs', value: money(r.local === null ? null : f.localJod, r.local) },
              { label: 'Total Costs', value: money(f.costs, costOwn), bold: true },
              {
                label: 'Estimated Profit',
                value: money(f.profit, costOwn === null ? null : r.price - costOwn),
                tone: 'success',
              },
              { label: 'Estimated Margin', value: f.margin === null ? 'Missing' : percent(f.margin), tone: 'success' },
            ]}
          />
        </Panel>
        <Panel title="Financial Status">
          <StageStepper
            steps={['Pricing Approved', 'JCS Approved', 'Invoiced', 'Paid']}
            current={current === -1 ? 4 : current}
            dates={[]}
          />
          <KeyValueList
            rows={[
              { label: 'Customer invoice', value: r.invoiceNo ? `${r.invoiceNo}: ${r.status}` : r.status },
              { label: 'Customer owes', value: f.owed ? jod(f.owed) : '–' },
              { label: 'Due date', value: f.invoiced ? shortDate(f.dueDate) : '–' },
              {
                label: 'Agent invoice',
                value: r.agentInvoiceNo
                  ? `${r.agentInvoiceNo}: ${r.agentPaid ? 'Paid' : r.agentVerified ? 'To pay' : 'Not checked yet'}`
                  : 'Not in yet',
              },
              { label: 'JCS', value: r.jcs },
            ]}
          />
          <Box sx={{ alignSelf: 'flex-start' }}>
            <StatusChip label={r.status} tone={STATUS_TONE[r.status]} />
          </Box>
        </Panel>
      </Box>
    </Stack>
  );
}

/** The two tiles about the profit on the shipments that belong to an agent or a carrier */
const relatedTiles = (s: ReturnType<typeof summarize>) => [
  {
    title: 'Gross Profit (Related)',
    note: 'On those shipments',
    value: jod(s.profit),
    icon: <TrendingUpOutlined />,
    tone: 'success' as const,
  },
  {
    title: 'Avg. Margin (Related)',
    note: 'On those shipments',
    value: percent(s.avgMargin),
    icon: <PercentOutlined />,
    tone: 'info' as const,
  },
];

/** A customer's revenue, costs, profit, what they owe and their shipments */
export function CustomerFinancials({ customer }: { customer: string }) {
  const fins = useFinance().filter((f) => f.ship?.customer === customer);
  const s = summarize(fins);
  const overdue = fins.filter((f) => f.overdue);
  return (
    <Stack spacing={2}>
      <ProfitTiles
        s={s}
        lg={4}
        xl={7}
        extra={[
          {
            title: 'Outstanding Invoices',
            note: 'Unpaid by this customer',
            value: jod(s.receivables),
            icon: <AccountBalanceWalletOutlined />,
            tone: 'warning',
          },
          {
            title: 'Overdue Invoices',
            note: `${overdue.length} invoice${overdue.length === 1 ? '' : 's'} past due`,
            value: jod(overdue.reduce((n, f) => n + f.owed, 0)),
            icon: <WarningAmberOutlined />,
            tone: 'error',
          },
        ]}
      />
      <ProfitChartPanel fins={fins} title="Profit Trend" />
      <ShipmentSummaryPanel fins={fins} title="Shipments (Financial Summary)" />
    </Stack>
  );
}

/** What an agent charged, what was paid, what is still owed, and the profit on the shipments it handled */
export function AgentFinancials({ agent }: { agent: string }) {
  const fins = useFinance().filter((f) => f.ship?.agent === agent);
  const handled = fins.filter((f) => f.r.agent !== null);
  const charges = handled.reduce((n, f) => n + f.agentJod, 0);
  const owed = handled.reduce((n, f) => n + f.agentOwed, 0);
  const s = summarize(fins);
  // the months the agent's invoices are dated in: charges, and the part already paid
  const months = [...new Set(handled.map((f) => f.date.slice(0, 7)))].sort();
  const inMonth = (m: string) => handled.filter((f) => f.date.slice(0, 7) === m);
  return (
    <Stack spacing={2}>
      <FinanceTiles
        tiles={[
          {
            title: 'Total Agent Charges',
            note: 'All shipments handled',
            value: jod(charges),
            icon: <ReceiptLongOutlined />,
            tone: 'info',
          },
          {
            title: 'Total Paid',
            note: 'Agent invoices paid',
            value: jod(charges - owed),
            icon: <PaymentsOutlined />,
            tone: 'success',
          },
          {
            title: 'Outstanding Payables',
            note: 'Not paid yet',
            value: jod(owed),
            icon: <AccountBalanceWalletOutlined />,
            tone: 'warning',
          },
          {
            title: 'Shipments Handled',
            note: 'With this agent',
            value: String(fins.length),
            icon: <LocalShippingOutlined />,
            tone: 'info',
          },
          ...relatedTiles(s),
        ]}
      />
      <Panel title="Agent Charges by Month">
        {months.length ? (
          <BarChart
            title="Agent charges by month"
            categories={months.map((m) => monthName(m + '-01'))}
            series={[
              { name: 'Charges', values: months.map((m) => inMonth(m).reduce((n, f) => n + f.agentJod, 0)) },
              {
                name: 'Paid',
                values: months.map((m) => inMonth(m).reduce((n, f) => n + (f.r.agentPaid ? f.agentJod : 0), 0)),
              },
            ]}
            format={jod}
          />
        ) : (
          <EmptyState>No agent invoices yet.</EmptyState>
        )}
      </Panel>
      <ShipmentSummaryPanel fins={fins} title="Related Shipments (Financial Summary)" />
    </Stack>
  );
}

/** What a carrier cost, and the profit on the shipments it carried, route by route */
export function CarrierFinancials({ carrier }: { carrier: string }) {
  const fins = useFinance().filter((f) => f.ship?.carrier === carrier);
  const freight = fins.reduce((n, f) => n + f.carrierJod, 0);
  const s = summarize(fins);
  const routes = [...new Set(fins.map((f) => (f.ship ? `${f.ship.from.city} → ${f.ship.to.city}` : '')))].filter(
    Boolean,
  );
  const onRoute = (route: string) => fins.filter((f) => f.ship && `${f.ship.from.city} → ${f.ship.to.city}` === route);
  return (
    <Stack spacing={2}>
      <FinanceTiles
        lg={3}
        xl={6}
        tiles={[
          {
            title: 'Shipments',
            note: 'With this carrier',
            value: String(fins.length),
            icon: <LocalShippingOutlined />,
            tone: 'info',
          },
          {
            title: 'Revenue (Related)',
            note: 'From those shipments',
            value: jod(s.revenue),
            icon: <PaymentsOutlined />,
            tone: 'success',
          },
          {
            title: 'Carrier Freight Costs',
            note: 'Charged by the carrier',
            value: jod(freight),
            icon: <ReceiptLongOutlined />,
            tone: 'error',
          },
          {
            title: 'Avg. Cost per Shipment',
            note: 'Carrier charges',
            value: fins.length ? jod(freight / fins.length) : '–',
            icon: <AccountBalanceWalletOutlined />,
            tone: 'warning',
          },
          ...relatedTiles(s),
        ]}
      />
      <Panel title="Costs by Route">
        {routes.length ? (
          <BarChart
            title="Costs by route"
            categories={routes}
            series={[
              { name: 'Cost', values: routes.map((r) => onRoute(r).reduce((n, f) => n + (f.costs ?? 0), 0)) },
              { name: 'Profit', values: routes.map((r) => onRoute(r).reduce((n, f) => n + (f.profit ?? 0), 0)) },
            ]}
            format={jod}
          />
        ) : (
          <EmptyState>No shipments with this carrier yet.</EmptyState>
        )}
      </Panel>
      <ShipmentSummaryPanel fins={fins} title="Related Shipments (Financial Summary)" />
    </Stack>
  );
}
